import { useAuth } from 'react-oidc-context';
import { useAdAttribution, useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import {
  BulkUpdateItemsResponse,
  ConsignmentApiModel,
  CreateCsvMappingResponse,
  CreateCustomersResponse,
  CreateItemResponse,
  CreateManifestResponse,
  CreateReturnCollectionResponse,
  CreateRuleResponse,
  CsvMappingApiModel,
  Customer,
  CustomerServiceTag,
  DeleteCsvMappingResponse,
  DeleteManifestResponse,
  DeleteRuleResponse,
  Dimensions,
  ExportItemsResponse,
  GetAccountPaymentOptionsResponse,
  GetAddressBreakdownApiResponseModel,
  GetAddressFullDetailsApiResponseModel,
  GetAwaitingDispatchItemsResponse,
  GetBookedItemsResponse,
  GetBookedStatusResponse,
  GetBulkProhibResponse,
  GetBulkQuoteValidationResponse,
  GetClaimResponse,
  GetClaimsResponse,
  GetCollectionDatesResponse,
  GetCsvMappingsResponse,
  GetCustomersResponse,
  GetDeliveredDutyPaidResponse,
  GetDropLocationResponse,
  GetDropLocationsResponse,
  GetInTransitItemsResponse,
  GetItemActionsResponse,
  GetItemByTrackingResponse,
  GetItemSnapshotsResponse,
  GetItemsResponse,
  GetLabelResponse,
  GetManifestDownloadResponse,
  GetManifestResponse,
  GetManifestsResponse,
  GetOrderActionsResponse,
  GetOrderResponse,
  GetOrdersResponse,
  GetOrderTrackingResponse,
  GetPackagesResponse,
  GetPaymentOptionBalanceResponse,
  GetPrintInStoreResponse,
  GetProductsResponse,
  GetQuoteOptionsResponse,
  GetQuoteProvidersResponse,
  GetQuoteValidationResponse,
  GetRefundsResponse,
  GetRulesResponse,
  GetServiceDetailsResponse,
  GetServiceTagsResponse,
  GetShipmentActionsResponse,
  GetSupportCasesResponse,
  GetTagsResponse,
  GetTrackingResponse,
  GroupItemsResponse,
  InputRuleApiModel,
  ItemCreateRequest,
  ItemRestriction,
  ManifestOrderLineApiMode,
  MarketplaceDispatchItemDetailsModel,
  MarketplaceProviderApiModel,
  PaymentOptionType,
  ProhibRequestItem,
  QuoteState,
  ReallocateManifestResponse,
  RestoreItemsResponse,
  RuleApiModel,
  SearchAddressesApiResponseModel,
  ShipResponse,
  SmartSendItemApiModel,
  SmartSendSettingsApiResponseModel,
  TariffClassificationApiModel,
  TariffCodeSuggestionApiResponse,
  TariffSearchApiModel,
  TariffSectionApiModel,
  TriggerAllImportsApiModel,
  UpdateCsvMappingResponse,
  UpdateCustomersResponse,
  UpdateManifestApiModel,
  UpdateManifestResponse,
  UpdateRuleResponse,
  UpdateServiceTagsResponse,
  VerifyShipResponse
} from './types';
import { getPath } from './get-path';

import { UnauthorisedResponse } from '../unauthorised-response';

import {
  addCache,
  clearDocCacheKey,
  clearItemCache,
  getCached,
  getOrAdd,
  getOrAddToQueue
} from '~/apps/smartsend/lib/helpers/ApiCache';
import { onlyUnique, SumNumber } from '~/lib/utils/array';
import { ItemExtrasAutoApplier } from '~/apps/smartsend/lib/helpers/ItemExtrasAutoApplier';
import { ApplierOption } from '~/apps/smartsend/lib/helpers/ApplierOption';
import { preprocessItemForShipping } from '~/apps/smartsend/lib/helpers/PreprocessItemForShipping';
import {
  SanitiseSmartSendItem,
  GenerateSmartSendUpdateModel
} from '~/apps/smartsend/lib/helpers/SmartSendUpdateHelper';
import { SmartSendProvider } from '~/lib/consts/providers';
import { parseResult } from '../parse-result';
import { ApiResponse } from '../api-response';
import { authedFetch } from '../authed-fetch';
import {
  SmartSendBookQueue,
  DefaultSmartSendQueue,
  SmartSendQuoteQueue,
  SmartSendStatsQueue,
  SmartSendTrackingQueue
} from './cache';
import { ddpQuoteKey } from './cache-keys';
import { useCallback } from 'react';
import { CACHE_PREFIX, VERSION } from '~/lib/constants';
import { getCookieValues, parseAbTastyCookie } from '~/lib/utils/analytics';
import { appendTenantFilterToQueryParams } from '~/apps/smartsend/components/smartsend/client/GetExternalSearchQueryParams';
import { extractReferringCustomerId } from '~/lib/utils/referral-program';
import { VatNumber } from '~/apps/smartsend/lib/consts/ItemMetaConstants';

interface MappedItem {
  mapped: SmartSendItemApiModel;
  key: string;
  senderValidationKey: string;
  recipientValidationKey: string;
  collectionDateValidationKey: string;
  cachePrefix?: string;
}

const csvMappingCacheKey = 'csvmappings';

function generateRulesKey(type: string) {
  return `RULES_${type}`;
}

function generateDimensionKey(d: Dimensions) {
  return `(${d?.Height || 0}|${d?.Width || 0}|${d?.Length || 0}|${d?.Weight || 0})`;
}

function generateConsignmentKey(c: ConsignmentApiModel) {
  return (
    `${c.Parcels.length}` +
    c.Parcels.map((x) => `${SumNumber(x.Contents.map((o) => o.Value))}|${generateDimensionKey(x.Dimensions)})`)
      .filter(onlyUnique)
      .join('cp')
  );
}

function cacheQuote(mappedItem: MappedItem, quote: QuoteState) {
  addCache('SESSIONSTORAGE', mappedItem.key, { ...quote, SenderValidation: [], RecipientValidation: [] });
  addCache('SESSIONSTORAGE', mappedItem.recipientValidationKey, [...quote.RecipientValidation]);
  addCache('SESSIONSTORAGE', mappedItem.senderValidationKey, [...quote.SenderValidation]);
}

function getCacheQuote(mappedItem: MappedItem): QuoteState {
  const quote: QuoteState = getCached<QuoteState>('SESSIONSTORAGE', mappedItem.key);
  if (!quote) {
    return null;
  }
  const output = {
    ...quote,
    RecipientValidation: getCached<InputRuleApiModel[]>('SESSIONSTORAGE', mappedItem.recipientValidationKey),
    SenderValidation: getCached<InputRuleApiModel[]>('SESSIONSTORAGE', mappedItem.senderValidationKey)
  };

  if (!output.RecipientValidation?.length || !output.SenderValidation?.length) {
    return null;
  }

  return output;
}

function generateMappedProhib(i: SmartSendItemApiModel) {
  const mapped: { key: string; mapped: ProhibRequestItem }[] = [];

  i.MarketplaceOrderLines.forEach((m) => {
    const map = {
      Id: m.Id,
      Service: i.Service.Id,
      Provider: i.Service.Provider,
      AccountId: i.Service.AccountId,
      SenderCountryIsoCode: i.Sender.CountryIsoCode,
      RecipientCountryIsoCode: i.Recipient.CountryIsoCode,
      Description: m.Product.Description
    };

    const key = [
      CACHE_PREFIX,
      'cv',
      map.Service,
      map.Provider,
      map.SenderCountryIsoCode,
      map.RecipientCountryIsoCode,
      map.Description
    ]
      .join('|')
      .toLowerCase();

    mapped.push({
      mapped: map,
      key: key
    });
  });

  return mapped;
}

export function generateItemQuoteRequestModel(item: SmartSendItemApiModel): MappedItem {
  const senderVatStatus = item.Sender.VatStatus;
  const recipientVatStatus = item.Recipient.VatStatus;

  const mapped = {
    Id: item.Id,
    Recipient: {
      CountryIsoCode: item.Recipient.CountryIsoCode,
      CountrySubdivision: item.Recipient.CountrySubdivision,
      Postcode: item.Recipient.Postcode,
      Phone: item.Recipient.Phone,
      VatStatus: recipientVatStatus
    },
    Sender: {
      CountryIsoCode: item.Sender.CountryIsoCode,
      CountrySubdivision: item.Sender.CountrySubdivision,
      Postcode: item.Sender.Postcode,
      Phone: item.Sender.Phone,
      VatStatus: senderVatStatus
    },
    Value: SumNumber(item.MarketplaceOrderLines.map((x) => x.Value)) || item.Value,
    MarketplaceOrderLines: item.MarketplaceOrderLines.map((x) => ({ Value: x.Value, Source: x.Source })),
    Service: item.Service,
    PackageDimensions: item.PackageDimensions,
    Extras: item.Extras,
    Consignment: item.Consignment,
    PreferredCollectionDate: item.PreferredCollectionDate
  };

  const key = [
    CACHE_PREFIX,
    'qv',
    mapped.PreferredCollectionDate || 'nocollectiondate',
    mapped.Service.Provider,
    mapped.Service.AccountId,
    mapped.Service.Id,
    mapped.Sender.CountryIsoCode,
    mapped.Sender.CountrySubdivision,
    mapped.Sender.Postcode,
    `${senderVatStatus}__${recipientVatStatus}`,
    mapped.Recipient.CountryIsoCode,
    mapped.Recipient.CountrySubdivision,
    mapped.Recipient.Postcode,
    mapped.Value,
    mapped.Consignment ? generateConsignmentKey(mapped.Consignment) : `con_0`,
    generateDimensionKey(mapped.PackageDimensions),
    mapped.Extras.map((e) => e.Id)
      .sort()
      .join('~')
  ]
    .join('|')
    .toLowerCase();

  const senderValidationKey = [
    CACHE_PREFIX,
    'av',
    'sender',
    mapped.Service.Provider,
    mapped.Service.AccountId,
    mapped.Service.Id,
    mapped.Sender.CountryIsoCode,
    mapped.Sender.CountrySubdivision,
    mapped.Recipient.CountryIsoCode,
    mapped.Recipient.CountrySubdivision,
    `${senderVatStatus}__${recipientVatStatus}`
  ]
    .join('|')
    .toLowerCase();

  const recipientValidationKey = [
    CACHE_PREFIX,
    'av',
    'recipient',
    mapped.Service.Provider,
    mapped.Service.AccountId,
    mapped.Service.Id,
    mapped.Recipient.CountryIsoCode,
    mapped.Recipient.CountrySubdivision,
    mapped.Sender.CountryIsoCode,
    mapped.Sender.CountrySubdivision,
    `${senderVatStatus}__${recipientVatStatus}`
  ]
    .join('|')
    .toLowerCase();

  const collectionDateValidationKey = [
    CACHE_PREFIX,
    'av',
    'collection-date',
    mapped.Service.Provider,
    mapped.Service.AccountId,
    mapped.Service.Id,
    mapped.Sender.CountryIsoCode,
    mapped.Sender.CountrySubdivision,
    mapped.Sender.Postcode
  ]
    .join('|')
    .toLowerCase();

  return {
    mapped,
    key,
    senderValidationKey,
    recipientValidationKey,
    collectionDateValidationKey,
    cachePrefix: CACHE_PREFIX
  } as MappedItem;
}

export function useSmartSendApi() {
  const { user } = useAuth();
  const analytics = useAnalytics();
  const adAttribution = useAdAttribution();
  const { tenant } = useTenant();

  const getItems = useCallback(async (): Promise<ApiResponse<GetItemSnapshotsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/smartsenditem/list`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<GetItemSnapshotsResponse>(response);
  }, [user, tenant]);

  const getSmartSendSettings = useCallback(async (): Promise<ApiResponse<SmartSendSettingsApiResponseModel>> => {
    try {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/setup`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<SmartSendSettingsApiResponseModel>(response);
    } catch {
      return { success: false } as ApiResponse<any>;
    }
  }, [user, tenant]);

  const getItemMarketplaceDispatchStatus = useCallback(
    async (id: string): Promise<ApiResponse<MarketplaceDispatchItemDetailsModel>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/MarketplaceDispatch/details-by-item/${id}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<MarketplaceDispatchItemDetailsModel>(response);
    },
    [user, tenant]
  );

  const getItemsMarketplaceDispatchStatus = useCallback(
    async (ids: string[]): Promise<ApiResponse<MarketplaceDispatchItemDetailsModel[]>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/MarketplaceDispatch/bulk/details-by-itemids`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify(ids) },
        user?.access_token
      );
      return parseResult<MarketplaceDispatchItemDetailsModel[]>(response);
    },
    [user, tenant]
  );

  const getCustomers = useCallback(async (): Promise<ApiResponse<GetCustomersResponse>> => {
    try {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/customer/list`);
      return getOrAdd(
        DefaultSmartSendQueue,
        'MEMORY',
        `${tenant}/saved_addresses`,
        () => authedFetch(tenant, url, {}, user?.access_token),
        1
      );
    } catch {
      return { success: false } as ApiResponse<any>;
    }
  }, [user, tenant]);

  const createDefaultCustomer = useCallback(
    async (customer: Customer): Promise<ApiResponse<CreateCustomersResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/customer`);
      customer.Meta['IsDefault'] = { Type: 'String', Value: 'true' };
      customer.Meta['Source'] = { Type: 'String', Value: 'SmartSend' };

      // SmartSend Edge Expects the VAT Number to be on the Customer object, not in the Meta field.
      // Temp Fix while resolve in API.
      if (!Object.keys(customer).includes(VatNumber)) {
        customer.VatNumber = customer.Meta[VatNumber]?.Value || '';
        delete customer.Meta[VatNumber];
      }

      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify(customer) },
        user?.access_token
      );
      return parseResult<CreateCustomersResponse>(response);
    },
    [user, tenant]
  );

  const getDeliveredDutyPaidCalculation = useCallback(
    async (
      provider: string,
      accountId: string,
      item: SmartSendItemApiModel,
      deliveryCostExcVat: number
    ): Promise<ApiResponse<GetDeliveredDutyPaidResponse>> => {
      const mapped = {
        Item: {
          ...item,
          State: null
        } as SmartSendItemApiModel,
        DeliveryCost: deliveryCostExcVat
      };

      const key = ddpQuoteKey(provider, accountId, mapped.Item as SmartSendItemApiModel);

      const url = getPath(
        tenant,
        `/extra/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/delivereddutypaid/calculate`
      );
      return getOrAdd(DefaultSmartSendQueue, 'SESSIONSTORAGE', key, () =>
        authedFetch(
          tenant,
          url,
          {
            method: 'POST',
            body: JSON.stringify(mapped)
          },
          user?.access_token
        )
      );
    },
    [user, tenant]
  );

  const getPackages = useCallback(async (): Promise<ApiResponse<GetPackagesResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }

    const url = getPath(tenant, `/package/list`);
    return getOrAdd(DefaultSmartSendQueue, 'MEMORY', 'package_list', () =>
      authedFetch(tenant, url, {}, user?.access_token)
    );
  }, [user, tenant]);

  const getQuoteOptions = useCallback(
    async (
      provider: string,
      accountId: string,
      item: SmartSendItemApiModel,
      serviceId?: string,
      includeTags?: string[],
      excludeTags?: string[]
    ): Promise<ApiResponse<GetQuoteOptionsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const mapped = {
        Recipient: {
          CountryIsoCode: item.Recipient.CountryIsoCode,
          CountrySubdivision: item.Recipient.CountrySubdivision,
          Postcode: item.Recipient.Postcode,
          VatStatus: item.Recipient.VatStatus
        },
        Sender: {
          CountryIsoCode: item.Sender.CountryIsoCode,
          CountrySubdivision: item.Sender.CountrySubdivision,
          Postcode: item.Sender.Postcode,
          VatStatus: item.Sender.VatStatus
        },
        Service: serviceId ? { Id: serviceId, Provider: provider, AccountId: accountId } : null,
        PackageDimensions: item.PackageDimensions,
        MarketplaceOrderLines: item.MarketplaceOrderLines.map((x) => {
          return { Value: x.Value, Source: x.Source };
        }),
        Value: SumNumber(item.MarketplaceOrderLines.map((x) => x.Value)) || item.Value,
        ServiceFilter: {
          IncludeServiceTags: includeTags || [],
          ExcludeServiceTags: excludeTags || []
        },
        Consignment: item.Consignment
      };

      const url = getPath(tenant, `/quote/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/options`);

      return getOrAddToQueue<GetQuoteOptionsResponse>(DefaultSmartSendQueue, () =>
        authedFetch(
          tenant,
          url,
          {
            method: 'POST',
            body: JSON.stringify(mapped)
          },
          user?.access_token,
          null,
          25000
        )
      );
    },
    [user, tenant]
  );

  const getQuoteWithValidation = useCallback(
    async (item: SmartSendItemApiModel): Promise<ApiResponse<GetQuoteValidationResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }

      const mapped = generateItemQuoteRequestModel(item);
      const cached = getCacheQuote(mapped);
      if (cached) {
        return { success: true, result: cached, statusCode: 200, validationErrors: null, error: null };
      }

      const url = getPath(tenant, `/quote/${mapped.mapped.Service.Provider}/${mapped.mapped.Service.AccountId}/full`);
      const response = await authedFetch(
        tenant,
        url,
        {
          method: 'POST',
          body: JSON.stringify(mapped.mapped)
        },
        user?.access_token
      );
      return parseResult<GetQuoteValidationResponse>(response);
    },
    [user, tenant]
  );

  const getBulkQuoteWithValidationInternal = useCallback(
    async (items: SmartSendItemApiModel[], timeout?: number): Promise<ApiResponse<GetBulkQuoteValidationResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }

      items.forEach((x) => ItemExtrasAutoApplier(x));

      const mapped: MappedItem[] = items.map((x) => generateItemQuoteRequestModel(x));

      const cached: { Id: string; Quote: QuoteState }[] = mapped
        .map((x) => ({ Id: x.mapped.Id, Quote: getCacheQuote(x) }))
        .filter((x) => x && x.Id && x.Quote && x.Quote.Quote);

      const unmapped = mapped
        .filter((x) => !cached.map((m) => m.Id).includes(x.mapped.Id))
        .map((x) => ({ Item: x.mapped, Provider: x.mapped.Service.Provider, AccountId: x.mapped.Service.AccountId }));

      const url = getPath(tenant, `/quote/bulk`);

      let response: ApiResponse<GetBulkQuoteValidationResponse>;
      if (unmapped.length > 0) {
        response = await getOrAddToQueue<GetBulkQuoteValidationResponse>(SmartSendQuoteQueue, () =>
          authedFetch(
            tenant,
            url,
            {
              method: 'POST',
              body: JSON.stringify({
                Items: unmapped
              })
            },
            user?.access_token,
            null,
            timeout
          )
        );
        if (response.success && response.result && response.result.Quotes) {
          for (const i in response.result.Quotes) {
            const ma = mapped.find((x) => x.mapped.Id === i);
            if (ma) {
              if (ma.key && !getCacheQuote(ma) && !!response.result.Quotes[i] && !!response.result.Quotes[i].Quote) {
                cacheQuote(ma, response.result.Quotes[i]);
              }
            }
          }
        }
      } else {
        response = {
          result: { Quotes: {} },
          success: true,
          statusCode: 200,
          validationErrors: null,
          error: null
        };
      }

      cached.forEach((c) => {
        response.result.Quotes[c.Id] = c.Quote;
      });

      return response;
    },
    [user, tenant]
  );

  const getBulkQuoteWithValidation = useCallback(
    async (items: SmartSendItemApiModel[]): Promise<ApiResponse<GetBulkQuoteValidationResponse>> => {
      try {
        return await getBulkQuoteWithValidationInternal(items, 25000);
      } catch (error) {
        if (['TimeoutError', 'AbortError'].includes(error?.name)) {
          console.log('Bulk Quote Timeout');
          return getBulkQuoteWithValidationInternal(items);
        }
        throw error;
      }
    },
    [getBulkQuoteWithValidationInternal]
  );

  const getProducts = useCallback(async (): Promise<ApiResponse<GetProductsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/product/list`);
    return getOrAdd<GetProductsResponse>(DefaultSmartSendQueue, 'MEMORY', 'products', () =>
      authedFetch(tenant, url, {}, user?.access_token)
    );
  }, [user, tenant]);

  const searchTariffCodes = useCallback(
    async (system: string, search: string): Promise<ApiResponse<TariffSearchApiModel>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/tariffcode/${system}/search/${search}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<TariffSearchApiModel>(response);
    },
    [user, tenant]
  );

  const getTariffCodeSections = useCallback(
    async (system: string): Promise<ApiResponse<TariffSectionApiModel[]>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/tariffcode/${system}/sections`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<TariffSectionApiModel[]>(response);
    },
    [user, tenant]
  );

  const getTariffCodeClassifications = useCallback(
    async (system: string, code: string): Promise<ApiResponse<TariffClassificationApiModel[]>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/tariffcode/${system}/classifications/${code}/children`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<TariffClassificationApiModel[]>(response);
    },
    [user, tenant]
  );

  const exportItems = useCallback(
    async (query: string, search?: string): Promise<ApiResponse<ExportItemsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      // `query` is the Algolia filter string; `search` is the free-text term, matching the export
      // endpoint's own naming.
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const url = getPath(
        tenant,
        `/smartsenditem/export?query=${encodeURIComponent(appendTenantFilterToQueryParams(query, tenant))}${searchParam}`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<ExportItemsResponse>(response);
    },
    [user, tenant]
  );

  const getCollectionDates = useCallback(
    async (item: SmartSendItemApiModel): Promise<ApiResponse<GetCollectionDatesResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/collectiondate`);
      const response = await authedFetch(
        tenant,
        url,
        {
          method: 'POST',
          body: JSON.stringify({
            ServiceSlug: item.Service.Id,
            Postcode: item.Sender.Postcode,
            CountryISO3Code: item.Sender.CountryIsoCode,
            DestinationCountryISO3Code: item.Recipient.CountryIsoCode
          })
        },
        user?.access_token
      );
      return parseResult<GetCollectionDatesResponse>(response);
    },
    [user, tenant]
  );

  const getRules = useCallback(
    async (type: 'SmartSend' | 'CsvMapping'): Promise<ApiResponse<GetRulesResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/rule/list/${type}`);
      return getOrAdd<GetRulesResponse>(
        SmartSendStatsQueue,
        'MEMORY',
        generateRulesKey(type),
        () => authedFetch(tenant, url, {}, user?.access_token),
        1
      );
    },
    [user, tenant]
  );

  const createRule = useCallback(
    async (rule: RuleApiModel): Promise<ApiResponse<CreateRuleResponse>> => {
      clearDocCacheKey(generateRulesKey(rule.RuleType));
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/rule/create`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify(rule) },
        user?.access_token
      );
      return parseResult<CreateRuleResponse>(response);
    },
    [user, tenant]
  );

  const updateRule = useCallback(
    async (rule: RuleApiModel): Promise<ApiResponse<UpdateRuleResponse>> => {
      clearDocCacheKey(generateRulesKey(rule.RuleType));
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/rule/update/${rule.Id}`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'PUT', body: JSON.stringify(rule) },
        user?.access_token
      );
      return parseResult<UpdateRuleResponse>(response);
    },
    [user, tenant]
  );

  const deleteRule = useCallback(
    async (id: number, ruleType: string): Promise<ApiResponse<DeleteRuleResponse>> => {
      clearDocCacheKey(generateRulesKey(ruleType));
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/rule/delete/${id}`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'DELETE', body: JSON.stringify({}) },
        user?.access_token
      );
      return parseResult<DeleteRuleResponse>(response);
    },
    [user, tenant]
  );

  const getCsvMappings = useCallback(async (): Promise<ApiResponse<GetCsvMappingsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/csv/mapping/list`);
    return getOrAdd(
      DefaultSmartSendQueue,
      'MEMORY',
      csvMappingCacheKey,
      () => authedFetch(tenant, url, {}, user?.access_token),
      10
    );
  }, [user, tenant]);

  const createCsvMapping = useCallback(
    async (mapping: CsvMappingApiModel): Promise<ApiResponse<CreateCsvMappingResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      clearItemCache('MEMORY', csvMappingCacheKey);
      const url = getPath(tenant, `/csv/mapping/create`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify(mapping) },
        user?.access_token
      );
      return parseResult<CreateCsvMappingResponse>(response);
    },
    [user, tenant]
  );

  const createDefaultCsvMapping = useCallback(async (): Promise<ApiResponse<CreateCsvMappingResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    clearItemCache('MEMORY', csvMappingCacheKey);
    const url = getPath(tenant, `/csv/mapping/create`);
    const response = await authedFetch(
      tenant,
      url,
      {
        method: 'POST',
        body: JSON.stringify({
          Name: 'Default Mapped Columns',
          Description: 'The set of default mapped columns for all of your CSV uploads.',
          Tags: ['Default'],
          Mappings: []
        })
      },
      user?.access_token
    );
    return parseResult<CreateCsvMappingResponse>(response);
  }, [user, tenant]);

  const updateCsvMapping = useCallback(
    async (mapping: CsvMappingApiModel): Promise<ApiResponse<UpdateCsvMappingResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      clearItemCache('MEMORY', csvMappingCacheKey);
      const url = getPath(tenant, `/csv/mapping/update/${mapping.Id}`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'PUT', body: JSON.stringify(mapping) },
        user?.access_token
      );
      return parseResult<UpdateCsvMappingResponse>(response);
    },
    [user, tenant]
  );

  const deleteCsvMapping = useCallback(
    async (id: number): Promise<ApiResponse<DeleteCsvMappingResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      clearItemCache('MEMORY', csvMappingCacheKey);
      const url = getPath(tenant, `/csv/mapping/delete/${id}`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'DELETE', body: JSON.stringify({}) },
        user?.access_token
      );
      return parseResult<DeleteCsvMappingResponse>(response);
    },
    [user, tenant]
  );

  const getItemByTracking = useCallback(
    async (tracking: string): Promise<ApiResponse<GetItemByTrackingResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/tracking/${tracking}/contents`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetItemByTrackingResponse>(response);
    },
    [user, tenant]
  );

  const getItemsByIds = useCallback(
    async (ids: string[]): Promise<ApiResponse<GetItemsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/list`);
      return getOrAddToQueue<GetItemsResponse>(DefaultSmartSendQueue, () =>
        authedFetch(tenant, url, { method: 'POST', body: JSON.stringify({ Ids: ids }) }, user?.access_token)
      );
    },
    [user, tenant]
  );

  const getBookedStatusByIds = useCallback(
    async (ids: string[]): Promise<ApiResponse<GetBookedStatusResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/shipping/booking-status`);
      return getOrAddToQueue<GetBookedStatusResponse>(DefaultSmartSendQueue, () =>
        authedFetch(tenant, url, { method: 'POST', body: JSON.stringify({ Ids: ids }) }, user?.access_token)
      );
    },
    [user, tenant]
  );

  const getBookedItems = useCallback(
    async (period: string, take: number, skip: number): Promise<ApiResponse<GetBookedItemsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/list/booked?period=${period}&take=${take}&skip=${skip}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetBookedItemsResponse>(response);
    },
    [user, tenant]
  );

  const getAwaitingDispatchItems = useCallback(
    async (period: string, take: number, skip: number): Promise<ApiResponse<GetAwaitingDispatchItemsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/list/awaitingdispatch?period=${period}&take=${take}&skip=${skip}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetAwaitingDispatchItemsResponse>(response);
    },
    [user, tenant]
  );

  const getInTransitItems = useCallback(
    async (period: string, take: number, skip: number): Promise<ApiResponse<GetInTransitItemsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/list/intransit?period=${period}&take=${take}&skip=${skip}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetInTransitItemsResponse>(response);
    },
    [user, tenant]
  );

  const getOrderItems = useCallback(
    async (
      provider: string,
      accountId: string,
      period: string,
      take: number,
      skip: number
    ): Promise<ApiResponse<GetOrdersResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/order/${encodeURIComponent(provider)}/${encodeURIComponent(
          accountId
        )}/list?period=${period}&take=${take}&skip=${skip}`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetOrdersResponse>(response);
    },
    [user, tenant]
  );

  const getDeletedItems = useCallback(async (): Promise<ApiResponse<GetItemsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/smartsenditem/list/deleted`);
    const response = await authedFetch(tenant, url, {}, user?.access_token);
    return parseResult<GetItemsResponse>(response);
  }, [user, tenant]);

  const getClaims = useCallback(async (): Promise<ApiResponse<GetClaimsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/claim/${SmartSendProvider}/${SmartSendProvider}/list`);
    return getOrAdd<GetClaimsResponse>(DefaultSmartSendQueue, 'SESSIONSTORAGE', 'claims', () =>
      authedFetch(tenant, url, {}, user?.access_token)
    );
  }, [user, tenant]);

  const getClaimDetails = useCallback(
    async (id: string): Promise<ApiResponse<GetClaimResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/claim/${SmartSendProvider}/${SmartSendProvider}/get/${id}`);
      return getOrAdd<GetClaimResponse>(DefaultSmartSendQueue, 'SESSIONSTORAGE', `claim_${id}`, () =>
        authedFetch(tenant, url, {}, user?.access_token)
      );
    },
    [user, tenant]
  );

  const getSupportCases = useCallback(async (): Promise<ApiResponse<GetSupportCasesResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/support/${SmartSendProvider}/${SmartSendProvider}/list`);
    return getOrAdd<GetSupportCasesResponse>(DefaultSmartSendQueue, 'SESSIONSTORAGE', 'supportcases', () =>
      authedFetch(tenant, url, {}, user?.access_token)
    );
  }, [user, tenant]);

  const updateCustomer = useCallback(
    async (id: string, c: Customer): Promise<ApiResponse<UpdateCustomersResponse>> => {
      try {
        if (!user?.access_token) {
          return UnauthorisedResponse;
        }
        const url = getPath(tenant, `/customer/update/${id}`);
        const response = await authedFetch(tenant, url, { method: 'PUT', body: JSON.stringify(c) }, user?.access_token);
        return parseResult<UpdateCustomersResponse>(response);
      } catch {
        return { success: false } as ApiResponse<any>;
      }
    },
    [user, tenant]
  );

  const suggest = useCallback(
    async (query: string): Promise<ApiResponse<TariffCodeSuggestionApiResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/customs/suggest?query=${encodeURIComponent(query)}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<TariffCodeSuggestionApiResponse>(response);
    },
    [user, tenant]
  );

  const getDropLocations = useCallback(
    async (
      provider: string,
      accountId: string,
      locationProvider: string,
      country: string,
      location: string,
      tag?: string | 'AcceptsDelivery' | undefined
    ): Promise<ApiResponse<GetDropLocationsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        tag
          ? `/droplocation/list/${encodeURIComponent(provider)}/${encodeURIComponent(
              accountId
            )}/${locationProvider}/${country}/${location}?tag=${encodeURIComponent(tag)}`
          : `/droplocation/list/${encodeURIComponent(provider)}/${encodeURIComponent(
              accountId
            )}/${locationProvider}/${country}/${location}`
      );

      return getOrAdd<GetDropLocationsResponse>(
        SmartSendStatsQueue,
        'SESSIONSTORAGE',
        `${CACHE_PREFIX}-drop-locations-${provider}-${accountId}-${locationProvider}-${country}-${location}-${tag ?? ''}`.toLocaleLowerCase(),
        () => authedFetch(tenant, url, {}, user?.access_token),
        15
      );
    },
    [user, tenant]
  );

  const getDropshop = useCallback(
    async (
      provider: string,
      accountId: string,
      locationProvider: string,
      id: string
    ): Promise<ApiResponse<GetDropLocationResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }

      const url = getPath(
        tenant,
        `/droplocation/get/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/${locationProvider}/${id}`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetDropLocationResponse>(response);
    },
    [user, tenant]
  );

  const bulkUpdateStatus = useCallback(
    async (ids: string[], status: 'Active' | 'Inactive' | 'Deleted'): Promise<ApiResponse<RestoreItemsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/bulk/status`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'PUT', body: JSON.stringify({ Ids: ids, Status: status }) },
        user?.access_token
      );
      return parseResult<RestoreItemsResponse>(response);
    },
    [user, tenant]
  );

  const getItemActions = useCallback(
    async (id: string): Promise<ApiResponse<GetItemActionsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/${id}/actions`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetItemActionsResponse>(response);
    },
    [user, tenant]
  );

  const getShipmentActions = useCallback(
    async (provider: string, accountId: string, id: string): Promise<ApiResponse<GetShipmentActionsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/${id}/actions`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetShipmentActionsResponse>(response);
    },
    [user, tenant]
  );

  const getManifests = useCallback(
    async (provider: string, accountId: string): Promise<ApiResponse<GetManifestsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/manifests/list`
      );
      const response = await authedFetch(tenant, url, { method: 'POST', body: JSON.stringify({}) }, user?.access_token);
      return parseResult<GetManifestsResponse>(response);
    },
    [user, tenant]
  );

  const getManifestDownload = useCallback(
    async (
      provider: string,
      accountId: string,
      manifestId: string
    ): Promise<ApiResponse<GetManifestDownloadResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/manifests/${encodeURIComponent(
          manifestId
        )}/download`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetManifestDownloadResponse>(response);
    },
    [user, tenant]
  );

  const createManifest = useCallback(
    async (provider: string, accountId: string, forDate: Date | null): Promise<ApiResponse<CreateManifestResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/manifests/create`
      );
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify({ ManifestOrderLines: [], ForDate: forDate }) },
        user?.access_token
      );
      return parseResult<CreateManifestResponse>(response);
    },
    [user, tenant]
  );

  const updateManifest = useCallback(
    async (
      provider: string,
      accountId: string,
      manifestId: string,
      manifest: UpdateManifestApiModel
    ): Promise<ApiResponse<UpdateManifestResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/manifests/${encodeURIComponent(
          manifestId
        )}/update`
      );
      const response = await authedFetch(
        tenant,
        url,
        { method: 'PUT', body: JSON.stringify(manifest) },
        user?.access_token
      );
      return parseResult<UpdateManifestResponse>(response);
    },
    [user, tenant]
  );

  const reallocateManifest = useCallback(
    async (
      provider: string,
      accountId: string,
      sourceManifestId: string,
      targetManifestId: string,
      orderlinesToReallocate: ManifestOrderLineApiMode[]
    ): Promise<ApiResponse<ReallocateManifestResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/manifests/${encodeURIComponent(
          sourceManifestId
        )}/reallocate`
      );
      const model = {
        ManifestOrderLinesToReallocate: orderlinesToReallocate,
        TargetManifestId: targetManifestId
      };
      const response = await authedFetch(
        tenant,
        url,
        { method: 'PUT', body: JSON.stringify(model) },
        user?.access_token
      );
      return parseResult<ReallocateManifestResponse>(response);
    },
    [user, tenant]
  );

  const deleteManifest = useCallback(
    async (provider: string, accountId: string, manifestId: string): Promise<ApiResponse<DeleteManifestResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/manifests/${encodeURIComponent(
          manifestId
        )}/delete`
      );
      const response = await authedFetch(
        tenant,
        url,
        { method: 'DELETE', body: JSON.stringify({}) },
        user?.access_token
      );
      return parseResult<DeleteManifestResponse>(response);
    },
    [user, tenant]
  );

  const getManifestById = useCallback(
    async (provider: string, accountId: string, manifestId: string): Promise<ApiResponse<GetManifestResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/manifests/${encodeURIComponent(
          manifestId
        )}/get`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetManifestResponse>(response);
    },
    [user, tenant]
  );

  const createReturnCollectionForSmartSendItem = useCallback(
    async (id: string): Promise<ApiResponse<CreateReturnCollectionResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/${id}/arrangereturncollection`);
      const response = await authedFetch(tenant, url, { method: 'POST', body: JSON.stringify({}) }, user?.access_token);
      return parseResult<CreateReturnCollectionResponse>(response);
    },
    [user, tenant]
  );

  const createReturnCollectionForOrder = useCallback(
    async (id: string): Promise<ApiResponse<CreateReturnCollectionResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/order/${SmartSendProvider}/${SmartSendProvider}/${id}/arrangereturncollection`);
      const response = await authedFetch(tenant, url, { method: 'POST', body: JSON.stringify({}) }, user?.access_token);
      return parseResult<CreateReturnCollectionResponse>(response);
    },
    [user, tenant]
  );

  const getOrderActions = useCallback(
    async (provider: string, accountId: string, id: string): Promise<ApiResponse<GetOrderActionsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/order/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/${id}/actions`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetOrderActionsResponse>(response);
    },
    [user, tenant]
  );

  const getPrintInStoreLinks = useCallback(
    async (provider: string, accountId: string, id: string): Promise<ApiResponse<GetPrintInStoreResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/order/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/${id}/print-in-store-barcodes`
      );

      return getOrAdd<GetPrintInStoreResponse>(SmartSendTrackingQueue, 'MEMORY', `${tenant}/print-in-store/${id}`, () =>
        authedFetch(tenant, url, {}, user?.access_token)
      );
    },
    [user, tenant]
  );

  const getFullOrder = useCallback(
    async (provider: string, accountId: string, id: string): Promise<ApiResponse<GetOrderResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/order/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/${id}/full`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetOrderResponse>(response);
    },
    [user, tenant]
  );

  const getQuoteProviders = useCallback(async (): Promise<ApiResponse<GetQuoteProvidersResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/quote/providers`);
    return getOrAdd<GetQuoteProvidersResponse>(
      SmartSendQuoteQueue,
      'SESSIONSTORAGE',
      `${CACHE_PREFIX}-${tenant}/quote_providers`,
      () => authedFetch(tenant, url, {}, user?.access_token)
    );
  }, [user, tenant]);

  const getServiceTags = useCallback(async (): Promise<ApiResponse<GetServiceTagsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/tag/service/list`);
    return getOrAdd(DefaultSmartSendQueue, 'SESSIONSTORAGE', 'service_tag_list', () =>
      authedFetch(tenant, url, {}, user?.access_token)
    );
  }, [user, tenant]);

  const updateServiceTags = useCallback(
    async (
      add: CustomerServiceTag[],
      remove: CustomerServiceTag[]
    ): Promise<ApiResponse<UpdateServiceTagsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const model = {
        Add: add,
        Remove: remove
      };
      clearItemCache('SESSIONSTORAGE', `service_tag_list`);
      const url = getPath(tenant, `/tag/service/update`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify(model) },
        user?.access_token
      );
      return parseResult<GetServiceTagsResponse>(response);
    },
    [user, tenant]
  );

  const getServiceDetails = useCallback(
    async (id: string, providerId: string, accountId: string): Promise<ApiResponse<GetServiceDetailsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/quote/${providerId}/${encodeURIComponent(accountId)}/${id}/details`);
      return getOrAdd<GetServiceDetailsResponse>(DefaultSmartSendQueue, 'SESSIONSTORAGE', `service_${id}`, () =>
        authedFetch(tenant, url, {}, user?.access_token)
      );
    },
    [user, tenant]
  );

  const getAccountPaymentOptions = useCallback(
    async (provider: string, accountId: string): Promise<ApiResponse<GetAccountPaymentOptionsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/paymentoption/account/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetAccountPaymentOptionsResponse>(response);
    },
    [user, tenant]
  );

  const getPaymentOptionBalance = useCallback(
    async (
      provider: string,
      accountId: string,
      method: PaymentOptionType
    ): Promise<ApiResponse<GetPaymentOptionBalanceResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/paymentoption/account/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/${method}/balance`
      );
      return getOrAdd<GetPaymentOptionBalanceResponse>(
        SmartSendStatsQueue,
        'SESSIONSTORAGE',
        `${tenant}/${provider}/${accountId}/${method}/balance`,
        () => authedFetch(tenant, url, {}, user?.access_token),
        10
      );
    },
    [user, tenant]
  );

  const getRefunds = useCallback(
    async (provider: string, accountId: string): Promise<ApiResponse<GetRefundsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/refund/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/list`);
      return getOrAdd<GetRefundsResponse>(
        SmartSendStatsQueue,
        'SESSIONSTORAGE',
        url,
        () => authedFetch(tenant, url, {}, user?.access_token),
        10
      );
    },
    [user, tenant]
  );

  const getTracking = useCallback(
    async (id: string): Promise<ApiResponse<GetTrackingResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/tracking/${id}`);
      return getOrAdd<GetTrackingResponse>(
        SmartSendTrackingQueue,
        'MEMORY',
        url,
        () => authedFetch(tenant, url, {}, user?.access_token),
        10
      );
    },
    [user, tenant]
  );

  const getOrderTracking = useCallback(
    async (provider: string, accountId: string, id: string): Promise<ApiResponse<GetOrderTrackingResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/order/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/${id}/tracking`
      );

      return getOrAdd<GetOrderTrackingResponse>(
        SmartSendTrackingQueue,
        'MEMORY',
        url,
        () => authedFetch(tenant, url, {}, user?.access_token),
        10
      );
    },
    [user, tenant]
  );

  const getLabels = useCallback(
    async (path: string): Promise<ApiResponse<GetLabelResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/${path}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetLabelResponse>(response);
    },
    [user, tenant]
  );

  const getTags = useCallback(async (): Promise<ApiResponse<GetTagsResponse>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/tag/list`);
    return getOrAdd(DefaultSmartSendQueue, 'SESSIONSTORAGE', 'tag_list', () =>
      authedFetch(tenant, url, {}, user?.access_token)
    );
  }, [user, tenant]);

  const prepareOrder = useCallback(
    async (
      provider: string,
      accountId: string,
      method: PaymentOptionType,
      items: SmartSendItemApiModel[],
      discountCode: string
    ): Promise<ApiResponse<ShipResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }

      let host: string | undefined = undefined;
      if (analytics.referrer) {
        try {
          host = new URL(analytics.referrer)?.hostname;
        } catch (e) {
          host = undefined;
        }
      }

      // Preprocess customs (e.g. strip EORI the quote doesn't require) before State (and its
      // QuoteState) is dropped - IBUG-2582.
      const mapped = items.map((i) => ({ ...preprocessItemForShipping(i), State: null }));

      const url = getPath(tenant, `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/prepare`);
      const req = {
        Items: mapped,
        DiscountCode: discountCode,
        PaymentMethod: method,
        MetaData: extractReferringCustomerId({
          device: analytics.id,
          qs: analytics.qs ?? undefined,
          referrer: host,
          session: analytics.session,
          path: analytics.path ?? undefined,
          appVersion: VERSION,
          ...getCookieValues(['_ga']),
          ...parseAbTastyCookie(),
          ...adAttribution
        })
      };
      return getOrAddToQueue<ShipResponse>(SmartSendBookQueue, () =>
        authedFetch(tenant, url, { method: 'POST', body: JSON.stringify(req) }, user?.access_token, {}, 30000)
      );
    },
    [user, tenant, analytics, adAttribution]
  );

  const completePreparedOrder = useCallback(
    async (
      provider: string,
      accountId: string,
      orderReference: string,
      method: PaymentOptionType,
      option: string,
      discountCode: string
    ): Promise<ApiResponse<ShipResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/complete`
      );
      const req = {
        Reference: orderReference,
        PaymentMethod: method,
        OptionId: option,
        DiscountCode: discountCode
      };

      const paymentTimeout = 20000;

      try {
        return await getOrAddToQueue<ShipResponse>(
          SmartSendBookQueue,
          async () =>
            await authedFetch(
              tenant,
              url,
              { method: 'POST', body: JSON.stringify(req) },
              user?.access_token,
              {},
              paymentTimeout
            )
        );
      } catch (error) {
        if (['TimeoutError', 'AbortError'].includes(error?.name)) {
          const order = await getShipmentActions(provider, accountId, orderReference);
          if (order.success && order.result?.BookingStatus != 'None') {
            const resp: ApiResponse<ShipResponse> = {
              statusCode: 200,
              error: null,
              result: order.result,
              success: true,
              validationErrors: null
            };
            return resp;
          }
        }
        throw error;
      }
    },
    [user, tenant, getShipmentActions]
  );

  const ship = useCallback(
    async (
      provider: string,
      accountId: string,
      items: SmartSendItemApiModel[],
      method: PaymentOptionType,
      option: string,
      discountCode: string
    ): Promise<ApiResponse<ShipResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      return prepareOrder(provider, accountId, method, items, discountCode).then((res) =>
        res.success && res.result?.BookingStatus == 'Prepared'
          ? completePreparedOrder(provider, accountId, res.result.Reference, method, option, discountCode)
          : res
      );
    },
    [user, prepareOrder, completePreparedOrder]
  );

  const verifyShip = useCallback(
    async (
      provider: string,
      accountId: string,
      items: SmartSendItemApiModel[],
      discountCode: string
    ): Promise<ApiResponse<VerifyShipResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/shipping/${encodeURIComponent(provider)}/${encodeURIComponent(accountId)}/verify`);
      // Preprocess customs (e.g. strip EORI the quote doesn't require) before State (and its
      // QuoteState) is dropped - IBUG-2582.
      const mapped = items.map((i) => ({ ...preprocessItemForShipping(i), State: null }));
      const req = {
        Items: mapped,
        DiscountCode: discountCode
      };
      return getOrAddToQueue<VerifyShipResponse>(SmartSendBookQueue, () =>
        authedFetch(tenant, url, { method: 'POST', body: JSON.stringify(req) }, user?.access_token)
      );
    },
    [user, tenant]
  );

  const getBulkProhib = useCallback(
    async (items: SmartSendItemApiModel[]): Promise<ApiResponse<GetBulkProhibResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      let mapped: { key: string; mapped: ProhibRequestItem }[] = [];
      items.map(generateMappedProhib).forEach((r) => {
        mapped = mapped.concat(r);
      });

      const cached: { Id: string; Result: ItemRestriction }[] = mapped
        .map((x) => {
          return { Id: x.mapped.Id, Result: getCached<ItemRestriction>('SESSIONSTORAGE', x.key) };
        })
        .filter((x) => !!x.Result);

      const itemsToQuery = mapped.filter((x) => !cached.map((x) => x.Id).includes(x.mapped.Id)).map((x) => x.mapped);

      const url = getPath(tenant, `/customs/bulk/cansend`);
      let response: ApiResponse<GetBulkProhibResponse>;

      if (itemsToQuery.length > 0) {
        response = await getOrAddToQueue<GetBulkProhibResponse>(DefaultSmartSendQueue, () =>
          authedFetch(
            tenant,
            url,
            {
              method: 'POST',
              body: JSON.stringify({
                Items: itemsToQuery
              })
            },
            user?.access_token
          )
        );
        if (response.success && response.result && response.result.Validation) {
          response.result.Validation.forEach((i) => {
            const key = mapped.find((x) => x.mapped.Id === i.Id)?.key;
            if (key) {
              addCache('SESSIONSTORAGE', key, i);
            }
          });
        }
      } else {
        response = {
          result: { Validation: [] },
          success: true,
          statusCode: 200,
          validationErrors: null,
          error: null
        };
      }

      cached.forEach((c) => {
        response.result.Validation.push(c.Result);
      });

      return response;
    },
    [user, tenant]
  );

  const createGroup = useCallback(
    async (items: SmartSendItemApiModel[]): Promise<ApiResponse<GroupItemsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/group`);
      return getOrAddToQueue(DefaultSmartSendQueue, () =>
        authedFetch(
          tenant,
          url,
          {
            method: 'POST',
            body: JSON.stringify({
              Items: items
                .map((i) => {
                  return {
                    Id: i.Id,
                    Recipient: i.Recipient,
                    MarketplaceOrderLines: i.MarketplaceOrderLines,
                    Source: i.Source,
                    Service: i.Service,
                    Sender: i.Sender,
                    Tags: i.Tags,
                    Extras: i.Extras,
                    PackageDimensions: i.PackageDimensions
                  };
                })
                .map(SanitiseSmartSendItem)
            })
          },
          user?.access_token
        )
      );
    },
    [user, tenant]
  );

  const addNewItems = useCallback(
    async (items: ItemCreateRequest): Promise<ApiResponse<CreateItemResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/create`);

      const sanitisedItems = { ...items };
      for (const x in sanitisedItems) {
        SanitiseSmartSendItem(sanitisedItems[x]);
      }

      return getOrAddToQueue(DefaultSmartSendQueue, () =>
        authedFetch(
          tenant,
          url,
          { method: 'POST', body: JSON.stringify({ Items: sanitisedItems }) },
          user?.access_token
        )
      );
    },
    [user, tenant]
  );

  const bulkUpdateItem = useCallback(
    async (
      items: { Item: SmartSendItemApiModel; Fields: ApplierOption[] }[]
    ): Promise<ApiResponse<BulkUpdateItemsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/bulk/update`);

      return getOrAddToQueue(DefaultSmartSendQueue, () =>
        authedFetch(
          tenant,
          url,
          {
            method: 'PUT',
            body: JSON.stringify({ Items: items.map((i) => GenerateSmartSendUpdateModel(i.Item, i.Fields)) })
          },
          user?.access_token
        )
      );
    },
    [user, tenant]
  );

  const searchAddresses = useCallback(
    async (isoCode: string, searchTerm: string): Promise<ApiResponse<SearchAddressesApiResponseModel>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/addresssearch/search/${isoCode}?searchTerm=${encodeURI(searchTerm)}&take=100`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<SearchAddressesApiResponseModel>(response);
    },
    [user, tenant]
  );

  const getAddressBreakdown = useCallback(
    async (
      isoCode: string,
      identifier: string,
      lastSearchTerm: string
    ): Promise<ApiResponse<GetAddressBreakdownApiResponseModel>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(
        tenant,
        `/addresssearch/breakdown/${isoCode}/${encodeURI(identifier)}?lastSearchTerm=${encodeURI(lastSearchTerm)}&take=100`
      );
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetAddressBreakdownApiResponseModel>(response);
    },
    [user, tenant]
  );

  const getAddressFullDetails = useCallback(
    async (isoCode: string, identifier: string): Promise<ApiResponse<GetAddressFullDetailsApiResponseModel>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/addresssearch/retrieve/${isoCode}/${encodeURI(identifier)}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetAddressFullDetailsApiResponseModel>(response);
    },
    [user, tenant]
  );

  const refreshItems = useCallback(
    async (ids: string[]): Promise<ApiResponse<GetItemSnapshotsResponse>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }
      const url = getPath(tenant, `/smartsenditem/list/${ids.join(',')}`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<GetItemSnapshotsResponse>(response);
    },
    [user, tenant]
  );

  const clearIntegrationCache = useCallback(() => {
    clearItemCache('MEMORY', `integrations`);
  }, []);

  const getMarketplaceIntegrations = useCallback(async (): Promise<ApiResponse<MarketplaceProviderApiModel>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/marketplace/providers`);
    return getOrAdd<MarketplaceProviderApiModel>(
      SmartSendStatsQueue,
      'MEMORY',
      `integrations`,
      () => authedFetch(tenant, url, {}, user?.access_token),
      1
    );
  }, [user, tenant]);

  const triggerAllImports = useCallback(async (): Promise<ApiResponse<TriggerAllImportsApiModel>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }
    const url = getPath(tenant, `/marketplace/trigger`);
    const response = await authedFetch(tenant, url, { method: 'POST' }, user?.access_token);
    return parseResult<TriggerAllImportsApiModel>(response);
  }, [user, tenant]);

  return {
    getItems,
    getSmartSendSettings,
    getCustomers,
    createDefaultCustomer,
    getDeliveredDutyPaidCalculation,
    getPackages,
    getQuoteOptions,
    getQuoteWithValidation,
    getBulkQuoteWithValidation,
    getProducts,
    clearIntegrationCache,
    triggerAllImports,
    getMarketplaceIntegrations,
    refreshItems,
    getAddressFullDetails,
    getAddressBreakdown,
    searchAddresses,
    bulkUpdateItem,
    addNewItems,
    createGroup,
    getBulkProhib,
    verifyShip,
    getLabels,
    getOrderTracking,
    getTracking,
    getRefunds,
    getPaymentOptionBalance,
    getAccountPaymentOptions,
    getServiceDetails,
    getTags,
    ship,
    updateServiceTags,
    getServiceTags,
    getQuoteProviders,
    getFullOrder,
    getOrderActions,
    getOrderItems,
    getInTransitItems,
    getAwaitingDispatchItems,
    getBookedItems,
    getDeletedItems,
    getClaims,
    getClaimDetails,
    getSupportCases,
    getManifestById,
    deleteManifest,
    updateManifest,
    createManifest,
    getManifestDownload,
    getManifests,
    getShipmentActions,
    getItemActions,
    bulkUpdateStatus,
    getDropLocations,
    suggest,
    updateCustomer,
    getItemsByIds,
    getBookedStatusByIds,
    getItemByTracking,
    deleteCsvMapping,
    updateCsvMapping,
    createCsvMapping,
    getCsvMappings,
    createReturnCollectionForOrder,
    createReturnCollectionForSmartSendItem,
    reallocateManifest,
    getDropshop,
    deleteRule,
    updateRule,
    createRule,
    getRules,
    getPrintInStoreLinks,
    getCollectionDates,
    exportItems,
    createDefaultCsvMapping,
    getTariffCodeClassifications,
    getTariffCodeSections,
    searchTariffCodes,
    getItemMarketplaceDispatchStatus,
    getItemsMarketplaceDispatchStatus
  } as const;
}
